import { FormattedNumber } from 'react-intl'
import { styled } from 'styled-components'

import {
  CurrencyStringCents,
  CurrencyStringDollars,
  FormattedMessage,
  InteractiveIcon,
} from '@b-stock/fe-core/bstock-react'
import {
  Breakpoints,
  Colors,
  pxToRem,
  Typography,
} from '@b-stock/fe-core/design-system'

const Body = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: 0.75rem 0;
`

const StatBox = styled.div`
  padding: 0 1rem;
  text-align: center;

  & + & {
    border-left: ${pxToRem(1)} solid ${Colors.Semantic.Neutral.medium_grey};
  }
`

const LabelRow = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
`

const StatLabel = styled.span`
  ${Typography.Overline3}
  color: ${Colors.Semantic.Neutral.dark_grey};
  letter-spacing: 0.05em;
  text-transform: uppercase;
`

const StatValue = styled.div`
  ${Typography.Title3}
  color: ${Colors.Semantic.Neutral.black};

  @media ${Breakpoints.max.medium} {
    ${Typography.Subtitle1}
  }
`

const StatSub = styled.div`
  ${Typography.Caption}
  color: ${Colors.Semantic.Neutral.black};
`

const InfoIcon = styled(InteractiveIcon)`
  color: ${Colors.Semantic.Neutral.dark_grey};
  font-size: ${pxToRem(10)};
  pointer-events: initial;
`

const ns = 'Listing.actionColumn.actionCard'

export const TlcStats = ({
  currency = 'USD',
  msrpPrice,
  percentMsrpTooltipId = `${ns}.tlcStats.percentMsrpTooltip`,
  costPerUnitTooltipId = `${ns}.tlcStats.costPerUnitTooltip`,
  tlcPercentOfMsrpWithTax,
  tlcPerUnitWithTax,
  units,
}: {
  currency?: string
  msrpPrice: number | undefined
  // Override the tooltip copy per card — offers use "offer" wording, the auction
  // bid box the default "Max Bid" wording.
  percentMsrpTooltipId?: string
  costPerUnitTooltipId?: string
  // Tax-inclusive per `getTlcTotals` — these render as given.
  tlcPercentOfMsrpWithTax: number | null
  tlcPerUnitWithTax: number | null
  units: number
}) => {
  if (tlcPercentOfMsrpWithTax === null && tlcPerUnitWithTax === null) {
    return null
  }

  return (
    <Body>
      <StatBox>
        <LabelRow>
          <StatLabel>
            <FormattedMessage id={`${ns}.tlcStats.percentMsrpLabel`} />
          </StatLabel>
          <InfoIcon
            size="small"
            tooltipProps={{ placement: 'bottom', hoverable: true }}
          >
            <FormattedMessage id={percentMsrpTooltipId} />
          </InfoIcon>
        </LabelRow>
        <StatValue>
          {tlcPercentOfMsrpWithTax !== null ? (
            <FormattedNumber
              value={tlcPercentOfMsrpWithTax}
              style="percent"
              minimumFractionDigits={1}
              maximumFractionDigits={1}
            />
          ) : (
            '—'
          )}
        </StatValue>
        {msrpPrice ? (
          <StatSub>
            <FormattedMessage
              id={`${ns}.tlcStats.msrpValue`}
              values={{
                value: (
                  <CurrencyStringCents value={msrpPrice} currency={currency} />
                ),
              }}
            />
          </StatSub>
        ) : null}
      </StatBox>

      <StatBox>
        <LabelRow>
          <StatLabel>
            <FormattedMessage id={`${ns}.tlcStats.costPerUnitLabel`} />
          </StatLabel>
          <InfoIcon
            size="small"
            tooltipProps={{ placement: 'bottom', hoverable: true }}
          >
            <FormattedMessage id={costPerUnitTooltipId} />
          </InfoIcon>
        </LabelRow>
        <StatValue>
          {tlcPerUnitWithTax !== null ? (
            <CurrencyStringDollars
              value={tlcPerUnitWithTax}
              currency={currency}
            />
          ) : (
            '—'
          )}
        </StatValue>
        {units > 0 ? (
          <StatSub>
            <FormattedMessage
              id={`${ns}.tlcStats.unitCount`}
              values={{ units }}
            />
          </StatSub>
        ) : null}
      </StatBox>
    </Body>
  )
}
